import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Loader2, TrendingUp, TrendingDown, ShoppingBag, Banknote, Percent, Box, BarChart3, Target, Trophy, Medal, Award, Calendar } from "lucide-react";

const fmtEur = (c) => ((c || 0) / 100).toFixed(2);
const fmtPct = (v) => `${((v || 0) * 100).toFixed(1)}%`;

export default function ShopDashboard() {
  const [period, setPeriod] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/shop/dashboard?period_months=${period}&top_n=10`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="admin-shop-dashboard">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-bold mb-2">Boutique · Analytics</div>
          <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">Tableau de bord</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Performances de votre boutique en ligne : CA, marge, produits stars, tendances d'achat.
          </p>
        </div>
        <Select value={period.toString()} onValueChange={v => setPeriod(+v)}>
          <SelectTrigger className="w-48" data-testid="period-select"><Calendar className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="3">3 derniers mois</SelectItem>
            <SelectItem value="6">6 derniers mois</SelectItem>
            <SelectItem value="12">12 derniers mois</SelectItem>
            <SelectItem value="24">24 derniers mois</SelectItem>
          </SelectContent>
        </Select>
      </header>

      {loading || !data ? (
        <Card className="p-16 border flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></Card>
      ) : (data.kpis.total_orders === 0) ? (
        <Card className="p-14 border text-center">
          <BarChart3 className="w-10 h-10 text-[hsl(var(--muted-foreground))]/30 mx-auto mb-3" />
          <div className="font-display font-bold text-[color:var(--brand-navy)]">Aucune donnée pour l'instant</div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Les indicateurs s'afficheront dès la première commande payée.</p>
        </Card>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi Icon={ShoppingBag} label="Commandes" value={data.kpis.total_orders} suffix="" tid="kpi-orders" />
            <Kpi Icon={Banknote} label="CA encaissé" value={fmtEur(data.kpis.total_revenue_cents)} suffix="€" tid="kpi-revenue" accent />
            <Kpi Icon={TrendingUp} label="Marge totale" value={fmtEur(data.kpis.total_margin_cents)} suffix="€" tid="kpi-margin" />
            <Kpi Icon={Percent} label="Taux de marge" value={fmtPct(data.kpis.margin_rate)} suffix="" tid="kpi-margin-rate" />
            <Kpi Icon={Target} label="Panier moyen" value={fmtEur(data.kpis.aov_cents)} suffix="€" tid="kpi-aov" />
            <Kpi Icon={Box} label="Articles vendus" value={data.kpis.total_units} suffix="" tid="kpi-units" />
            <Kpi Icon={Trophy} label="Produits distincts" value={data.kpis.products_count_sold} suffix="" tid="kpi-products-sold" />
            <Kpi Icon={Banknote} label="Coût total (COGS)" value={fmtEur(data.kpis.total_cost_cents)} suffix="€" tid="kpi-cost" />
          </div>

          {/* Trend chart */}
          <Card className="p-5 border" data-testid="trend-chart-card">
            <h3 className="font-display font-bold flex items-center gap-2 mb-4"><BarChart3 className="w-4 h-4" />Tendance mensuelle — CA & Marge</h3>
            <TrendChart trends={data.monthly_trends} />
          </Card>

          {/* Top lists */}
          <div className="grid lg:grid-cols-3 gap-3">
            <TopList title="Top ventes (quantité)" Icon={Trophy} metric="qty" list={data.top_by_qty} formatVal={v => `${v} u.`} tid="top-qty" />
            <TopList title="Top CA" Icon={Medal} metric="revenue" list={data.top_by_revenue} formatVal={v => `${fmtEur(v)} €`} tid="top-revenue" />
            <TopList title="Top marges" Icon={Award} metric="margin" list={data.top_by_margin} formatVal={v => `${fmtEur(v)} €`} tid="top-margin" />
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ Icon, label, value, suffix, tid, accent = false }) {
  return (
    <Card className={`p-4 border ${accent ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]" : ""}`} data-testid={tid}>
      <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold ${accent ? "text-white/70" : "text-[hsl(var(--muted-foreground))]"}`}>
        <Icon className="w-3 h-3" />
        <span>{label}</span>
      </div>
      <div className={`font-display text-2xl lg:text-3xl font-black tabular-nums mt-0.5 ${accent ? "text-white" : "text-[color:var(--brand-navy)]"}`}>{value}{suffix && <span className="text-base ml-0.5 opacity-70">{suffix}</span>}</div>
    </Card>
  );
}

function TrendChart({ trends }) {
  const maxRev = Math.max(1, ...trends.map(t => t.revenue_cents));
  const maxMar = Math.max(1, ...trends.map(t => Math.abs(t.margin_cents)));
  return (
    <div>
      <div className="grid" style={{ gridTemplateColumns: `repeat(${trends.length}, minmax(0, 1fr))`, gap: "6px", alignItems: "end", minHeight: "180px" }}>
        {trends.map((t) => {
          const revH = Math.round((t.revenue_cents / maxRev) * 160);
          const marH = Math.round((Math.abs(t.margin_cents) / maxMar) * 160);
          return (
            <div key={t.label} className="flex flex-col items-center gap-1" data-testid={`trend-month-${t.label}`}>
              <div className="text-[10px] font-bold text-[color:var(--brand-navy)] tabular-nums whitespace-nowrap">{fmtEur(t.revenue_cents)}€</div>
              <div className="flex gap-0.5 items-end" style={{ height: "160px" }}>
                <div className="w-3 bg-[color:var(--brand-sky)] rounded-t" style={{ height: `${Math.max(2, revH)}px` }} title={`CA: ${fmtEur(t.revenue_cents)} €`} />
                <div className="w-3 bg-emerald-500 rounded-t" style={{ height: `${Math.max(2, marH)}px` }} title={`Marge: ${fmtEur(t.margin_cents)} €`} />
              </div>
              <div className="text-[9px] text-[hsl(var(--muted-foreground))] font-mono">{t.label}</div>
              <div className="text-[9px] text-[hsl(var(--muted-foreground))]">{t.orders} cmd</div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-center gap-4 mt-3 text-[11px]">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[color:var(--brand-sky)]" />CA</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500" />Marge</span>
      </div>
    </div>
  );
}

function TopList({ title, Icon, metric, list, formatVal, tid }) {
  const field = metric === "qty" ? "qty" : (metric === "revenue" ? "revenue_cents" : "margin_cents");
  return (
    <Card className="p-4 border" data-testid={tid}>
      <h4 className="font-display font-bold flex items-center gap-2 mb-3 text-sm"><Icon className="w-4 h-4" />{title}</h4>
      {list.length === 0 ? (
        <div className="text-xs text-[hsl(var(--muted-foreground))] text-center py-6">Aucune donnée</div>
      ) : (
        <div className="space-y-1.5">
          {list.map((p, i) => {
            const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`;
            return (
              <div key={p.product_id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[hsl(var(--muted))]/30" data-testid={`${tid}-row-${i}`}>
                <span className="text-[10px] font-bold w-6 shrink-0">{medal}</span>
                {p.featured_image ? (
                  <img src={p.featured_image} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded bg-[color:var(--brand-sky)]/10 flex items-center justify-center shrink-0"><Box className="w-3.5 h-3.5 text-[color:var(--brand-sky)]" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-[color:var(--brand-navy)] truncate">{p.name}</div>
                  <div className="text-[10px] text-[hsl(var(--muted-foreground))]">
                    {p.orders_count} cmd · {fmtPct(p.revenue_share)} du CA
                    {metric !== "margin" && <> · marge {fmtPct(p.margin_rate)}</>}
                  </div>
                </div>
                <div className="font-bold text-sm text-[color:var(--brand-navy)] tabular-nums shrink-0">{formatVal(p[field])}</div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
